<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <v-card class="pb-4" outlined>
      <v-card-title class="py-1" :style="{ 'background-color': '#eeeeee' }">
        {{ $t('MSG0081') }}
      </v-card-title>
      <v-card-text class="py-1">
        <v-container class="form-mmmm-supplier px-2 py-2" fluid>
          <v-form :disabled="!canEditContents">
            <v-row>
              <v-col cols="2">
                <PartGroupInput
                  v-model="form.partGroupCd"
                  :validOnly="canEditContents"
                  required
                  @change="onChangePartGroup"
                />
              </v-col>
              <v-col cols="2">
                <MmmmApplCatInput
                  v-model="form.applCatCd"
                  :validOnly="canEditContents"
                  required
                  @inputModel="onChangeApplCat"
                  :disabled="hasGeneratedMmmmNo"
                />
              </v-col>
              <v-col cols="2">
                <SelectSingleInput
                  v-model="form.applRequestByCd"
                  :label="$t('ELE0448')"
                  :items="companyCatCdItems"
                  itemText="name"
                  itemValue="value"
                />
              </v-col>
              <v-col cols="2">
                <SectionInput
                  v-model="form.sectionCd"
                  :validOnly="canEditContents"
                  required
                  :disabled="hasGeneratedMmmmNo"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <MmmmReasonInput
                  v-model="form.reasonCd"
                  :applCatCd="form.applCatCd"
                  :validOnly="canEditContents"
                  required
                />
              </v-col>
              <v-col cols="8">
                <MmmmReasonDtlInput
                  v-model="form.reasonDtlCd"
                  :reasonCd="form.reasonCd"
                  :validOnly="canEditContents"
                  required
                  @change="onChangeReasonDtlCd"
                />
              </v-col>
            </v-row>
          </v-form>

          <br />

          <v-row>
            <v-col cols="8">
              <TextAreaInput v-if="canEditRemarks" :label="$t('ELE0054')" v-model="form.remarks" :maxLength="1000" />
              <TextAreaRef v-else :label="$t('ELE0054')" :value="form.remarks" />
            </v-col>
          </v-row>

          <br />

          <v-form :disabled="!canEditContents">
            <v-row>
              <v-col cols="4">
                <ManufacturerInput v-model="form.manufacturerCd" :validOnly="canEditContents" required />
              </v-col>
              <v-col cols="2">
                <TextFieldInputWithDatePicker
                  :label="$t('ELE0223')"
                  v-model="form.issueDate"
                  disableFutureDate
                  required
                />
              </v-col>
              <v-col cols="2">
                <TextFieldInputWithDatePicker :label="$t('ELE0449')" v-model="form.targetUsageDate" clearable />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <TextFieldRef :label="$t('ELE0443')" :value="form.severityName" />
              </v-col>
              <v-col cols="2">
                <TextFieldInput :label="$t('ELE0450')" v-model="form.tenNo" :maxLength="50" />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-card elevation="1" max-width="1200" outlined>
                  <v-card-title class="py-0">
                    {{ $t('ELE0484') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <v-row>
                      <v-col cols="4">
                        <PtCdInput
                          v-model="form.ptCd"
                          :supplierCd="form.manufacturerCd"
                          :partGroupCd="form.partGroupCd"
                          :validOnly="canEditContents"
                          required
                          @change="onChangePtCd"
                        />
                      </v-col>
                      <v-col>
                        <MoldNoInput
                          v-if="canEditContents"
                          v-model="form.moldNo"
                          :ptCd="form.ptCd"
                          validOnly
                          @inputModel="onChangeMoldNo"
                        />
                        <TextFieldRef v-else :label="$t('ELE0051')" :value="form.moldNo" />
                      </v-col>
                      <v-col>
                        <PartCatInput :value="form.partCatCd" disabled />
                      </v-col>
                      <v-col>
                        <TextFieldRef :label="$t('ELE0224')" :value="form.model" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <MmmmLotInput
                          v-if="canEditContents"
                          v-model="form.lotNo"
                          :inspTimingCd="enumOqcIqcCd.OQC"
                          :manufacturerCd="form.manufacturerCd"
                          :partCd="form.partCd"
                          :inspStatusCd="enumInspStatusCd.COMPLETE"
                          isMmmmUnapproved
                          required
                          @change="onChangeLotNo"
                        />
                        <TextFieldRef v-else :label="$t('ELE0049')" :value="form.lotNo" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0513')" v-model="form.drawingNo" :maxLength="20" />
                      </v-col>
                      <v-col>
                        <TextFieldInput
                          v-if="canEditContents && form.reqSampleAttached"
                          :label="$t('ELE0451')"
                          v-model="form.sampleAttachedQty"
                          right
                          rules="rangeNumber:1,9999999"
                          required
                        />
                        <TextFieldRef v-else :label="$t('ELE0451')" :value="form.sampleAttachedQty" />
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <MmmmInspIndexInput
                          v-if="canEditContents"
                          v-model="form.oqcInspIndexNo"
                          :inspTimingCd="enumOqcIqcCd.OQC"
                          :manufacturerCd="form.manufacturerCd"
                          :partCd="form.partCd"
                          :lotNo="form.lotNo"
                          :inspStatusCd="enumInspStatusCd.COMPLETE"
                          required
                        />
                        <TextFieldRef v-else :label="$t('ELE0137')" :value="form.oqcInspIndexNo" />
                      </v-col>
                      <v-col>
                        <Btn :label="$t('ELE0307')" :disabled="form.oqcInspIndexNo == ''" @click="showInspResult" />
                      </v-col>
                      <v-col> </v-col>
                      <v-col> </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>

          <br />

          <v-form :disabled="!canEditContents">
            <v-row class="my-0">
              <v-col>
                <v-card elevation="1" max-width="1200" outlined>
                  <v-card-title class="py-0">
                    {{ $t('ELE0485') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0444')" v-model="form.plPr.numOfCav" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0445')" v-model="form.plPr.cavNo" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0452')" v-model="form.plPr.cycleTime" :maxLength="50" />
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0454')" v-model="form.plPr.mcNo" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0455')" v-model="form.plPr.mcBrand" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0456')" v-model="form.plPr.tonnage" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <SelectSingleInput
                          v-model="form.plPr.productTakeOutCd"
                          :label="$t('ELE0453')"
                          :items="productTakeOutItems"
                          itemText="name"
                          itemValue="value"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0458')" v-model="form.plPr.matlSupplier" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0459')" v-model="form.plPr.matlGrade" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0460')" v-model="form.plPr.color" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <SelectSingleInput
                          v-model="form.plPr.injectionMcTypeCd"
                          :label="$t('ELE0457')"
                          :items="injectionMCTypeItems"
                          itemText="name"
                          itemValue="value"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0461')" v-model="form.plPr.partsWeight" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0462')" v-model="form.plPr.sprueWeight" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0463')" v-model="form.plPr.scrapWeight" :maxLength="50" />
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="my-0">
              <v-col>
                <v-card elevation="1" max-width="1200" outlined>
                  <v-card-title class="py-0">
                    {{ $t('ELE0486') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <v-row>
                      <v-col>
                        <SelectMultipleInput
                          :label="$t('ELE0093')"
                          v-model="secondProcess.secondCatCds"
                          :items="secondaryCategoryItems"
                          itemText="name"
                          itemValue="value"
                      /></v-col>
                      <v-col cols="6">
                        <TextFieldInput :label="$t('ELE0464')" v-model="secondProcess.othersDetail" :maxLength="1000" />
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0 d-flex justify-end">
                        <BtnAdd :disabled="!canEditContents" @click="onAddSecondPart" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-card class="mx-2" outlined>
                          <v-data-table
                            :headers="headersSecondPart"
                            :items="secondProcess.parts"
                            dense
                            disable-pagination
                            hide-default-footer
                            mobile-breakpoint="0"
                          >
                            <template v-slot:[`item.action`]="{ item, index }">
                              <IconDelete :disabled="!canEditContents" @click="onDeleteSecondPart(index)" />
                            </template>
                            <template v-slot:[`item.partCd`]="{ item, index }">
                              <PartCodeInput
                                v-if="isEpson"
                                :vid="'secondPart_' + index"
                                label=""
                                v-model="item.partCd"
                                :validOnly="canEditContents"
                                required
                              />
                              <SupplierPartInput
                                v-else
                                :vid="'secondPart_' + index"
                                label=""
                                v-model="item.partCd"
                                supplierCd=""
                                isSearchByLoginCompany
                                :validOnly="canEditContents"
                                required
                              />
                            </template>
                            <template v-slot:[`item.manufacturerCd`]="{ item, index }">
                              <PartSupplierInput
                                :vid="'secondManufacturer_' + index"
                                label=""
                                v-model="item.manufacturerCd"
                                :partCd="item.partCd"
                                :validOnly="canEditContents"
                                required
                              />
                            </template>
                            <template v-slot:[`item.remarks`]="{ item }">
                              <TextFieldInput label="" v-model="item.remarks" :maxLength="1000" />
                            </template>
                          </v-data-table>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="my-0">
              <v-col>
                <v-card elevation="1" max-width="1200" outlined>
                  <v-card-title class="py-0">
                    {{ $t('ELE0487') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0465')" v-model="form.paint.paintColor" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0466')" v-model="form.paint.paintCode" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput
                          :label="$t('ELE0467')"
                          v-model="form.paint.paintSpecification"
                          :maxLength="50"
                        />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0468')" v-model="form.paint.paintSupplier" :maxLength="50" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <SelectSingleInput
                          v-model="form.paint.paintProcessCd"
                          :label="$t('ELE0469')"
                          :items="paintingProcessItems"
                          itemText="name"
                          itemValue="value"
                        />
                      </v-col>
                      <v-col> </v-col>
                      <v-col> </v-col>
                      <v-col> </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="my-0">
              <v-col>
                <v-card elevation="1" max-width="1200" outlined>
                  <v-card-title class="py-0">
                    {{ $t('ELE0488') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <v-row>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0471')" v-model="form.print.inkColor" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0472')" v-model="form.print.inkCode" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0473')" v-model="form.print.inkSpecification" :maxLength="50" />
                      </v-col>
                      <v-col>
                        <TextFieldInput :label="$t('ELE0474')" v-model="form.print.inkSupplier" :maxLength="50" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <SelectSingleInput
                          v-model="form.print.printProcessCd"
                          :label="$t('ELE0475')"
                          :items="inkProcessItems"
                          itemText="name"
                          itemValue="value"
                        />
                      </v-col>
                      <v-col cols="6">
                        <TextFieldInput :label="$t('ELE0470')" v-model="form.print.remarks" :maxLength="1000" />
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="my-0">
              <v-col>
                <v-card class="pb-2" elevation="1" max-width="1200px" outlined>
                  <v-card-title class="py-1">
                    {{ $t('ELE0498') }}
                    <v-spacer />
                    <BtnAdd :disabled="!canEditContents" @click="onAddDoc" />
                  </v-card-title>
                  <v-card class="mx-2" outlined>
                    <v-data-table
                      :headers="headersDoc"
                      :items="requiredDocs"
                      dense
                      disable-pagination
                      hide-default-footer
                      mobile-breakpoint="0"
                    >
                      <template v-slot:[`item.action`]="{ item, index }">
                        <IconDelete :disabled="!canEditContents || item.docCatCd != ''" @click="onDeleteDoc(index)" />
                      </template>
                      <template v-slot:[`item.docCatName`]="{ item, index }">
                        <TextFieldInput
                          v-if="item.docCatCd == ''"
                          :vid="'docCatName_' + index"
                          label=""
                          v-model="item.docCatName"
                          :maxLength="40"
                          required
                        />
                        <TextFieldRef v-else label="" :value="item.docCatName" />
                      </template>
                      <template v-slot:[`item.filePath`]="{ item, index }">
                        <FileInput
                          :vid="'filePath_' + index"
                          label=""
                          v-model="item.file"
                          :filePath.sync="item.filePath"
                          required
                          :clearable="canEditContents"
                        />
                      </template>
                    </v-data-table>
                  </v-card>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-card elevation="1" max-width="1200px" outlined>
                  <MmmmRequestRouteForm
                    :companyCd="form.requestCompanyCd"
                    :companyCatCd="form.requestCompanyCatCd"
                    :currentRouteNo="form.currentRouteNo"
                    :currentRouteDtlNo="form.currentRouteDtlNo"
                    :routeNo="form.supplierRoute.routeNo"
                    :checkers.sync="form.supplierRoute.checkers"
                    :disabled="!canEditRoute"
                    :title="$t('ELE0233')"
                    outlined
                  />
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <UserInput
                  :label="$t('ELE0238')"
                  v-model="form.accepterCd"
                  :companyCat="enumCompanyCatCd.EPSON"
                  :roleCdList="[enumRole.MMMM_CHECK, enumRole.MMMM_APPROVE]"
                  :validOnly="canEditRoute"
                  required
                />
              </v-col>
            </v-row>
          </v-form>

          <v-row v-if="canEditRoute" class="py-2">
            <v-col class="d-flex justify-end">
              <Btn
                class="mr-8"
                :label="$t('ELE0036')"
                :disabled="invalid || !canDelete"
                isCheckRole
                @click="onClickDelete"
              />
              <Btn
                class="mx-4"
                :label="$t('ELE0247')"
                :disabled="invalid || !canTempSave"
                isCheckRole
                @click="onClickTempSave"
              />
              <Btn
                class="mx-4"
                :label="$t('ELE0248')"
                :disabled="invalid || !canRequest"
                isCheckRole
                @click="onClickRequest"
              />
              <Btn
                class="mx-4"
                :label="$t('ELE0215')"
                :disabled="invalid || !canApprove"
                isCheckRole
                @click="onClickApprove"
              />
              <Btn
                class="mx-4"
                :label="$t('ELE0249')"
                :disabled="invalid || !canDeny"
                isCheckRole
                @click="onClickDeny"
              />
              <Btn
                class="mx-4"
                :label="$t('ELE0250')"
                :disabled="invalid || !canReturnToRequest"
                isCheckRole
                @click="onClickReturnToRequest"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <OqcResultDialog
      v-if="oqcResultDialog"
      :dialog.sync="oqcResultDialog"
      :inspIndexNo="inspResult.inspIndexNo"
      :inspCompanyCd="inspResult.inspCompanyCd"
      :lotNo="inspResult.lotNo"
      :partCd="form.partCd"
      :partName="form.partName"
      :inspCat1="inspResult.inspCat1"
      :inspCat2="inspResult.inspCat2"
      :lotDate="inspResult.lotDate"
      inspDataViewOnly
    />
    <DelConfirmDialog
      :dialog="delConfirmDialog"
      :confirmWord="$t('MSG0013')"
      :btnLabel="$t('ELE0036')"
      @cancel="delConfirmDialog = false"
      @proceed="onProceedDelete"
    />
    <UserConfirmDialog
      :dialog="userConfirmDialog"
      :confirmWord="$t('MSG0090')"
      :btnLabel="$t('ELE0019')"
      @cancel="userConfirmDialog = false"
      @proceed="onProceedSave"
    />
  </validation-observer>
</template>

<script>
import { mapGetters } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import {
  Role,
  S3Root,
  CompanyCatCd,
  OqcIqcCd,
  MmmmProductTakeOut,
  MmmmInjectionMCType,
  MmmmSecondaryCategory,
  MmmmPaintingProcess,
  MmmmInkProcess,
  MmmmApprovalStatusCd,
  MmmmWfCheckedCat,
  InspStatusCd,
} from '@/enum'
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import { Storage } from '@/plugins/storage'
import DateUtil from '@/mixin/date-utils.js'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import TextAreaRef from '@/components/atoms/base/text-field/TextAreaRef'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import Btn from '@/components/atoms/base/button/Btn'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import BtnDelete from '@/components/atoms/button/BtnDelete'
import IconDelete from '@/components/atoms/icon/IconDelete.vue'
import FileInput from '@/components/atoms/file/FileInput'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import SelectMultipleInput from '@/components/atoms/base/select/SelectMultipleInput'

import PartGroupInput from '@/components/atoms/select/PartGroupInput'
import MmmmApplCatInput from '@/components/atoms/select/MmmmApplCatInput'
import SectionInput from '@/components/atoms/select/SectionInput'
import MmmmReasonInput from '@/components/atoms/select/MmmmReasonInput'
import MmmmReasonDtlInput from '@/components/atoms/select/MmmmReasonDtlInput'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import PtCdInput from '@/components/atoms/select/PtCdInput'
import MoldNoInput from '@/components/atoms/select/MoldNoInput'
import PartCatInput from '@/components/atoms/select/PartCatInput'
import MmmmLotInput from '@/components/atoms/select/MmmmLotInput'
import MmmmInspIndexInput from '@/components/atoms/select/MmmmInspIndexInput'
import MmmmRequiredDocInput from '@/components/atoms/select/MmmmRequiredDocInput'
import UserInput from '@/components/atoms/select/UserInput'
import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import PartSupplierInput from '@/components/atoms/select/PartSupplierInput'

import OqcResultDialog from '@/components/organisms/terminal/dialog/ApprovalInspDataListDialog'
import MmmmRequestRouteForm from '@/components/organisms/terminal/form/MmmmRequestRouteForm'
import DelConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'
import UserConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'

/** 処理モード */
export const SaveMode = {
  TEMP_SAVE: 'TEMP_SAVE',
  REQUEST: 'REQUEST',
  APPROVE: 'APPROVE',
  DENY: 'DENY',
  RETURN: 'RETURN',
}

export default {
  name: 'MmmmRequestSupplierForm',

  mixins: [APIUtils, Message, Loading, DateUtil],

  components: {
    ValidationObserver,
    TextFieldInput,
    TextFieldRef,
    TextAreaInput,
    TextAreaRef,
    TextFieldInputWithDatePicker,
    Btn,
    BtnAdd,
    BtnDelete,
    IconDelete,
    FileInput,
    SelectSingleInput,
    SelectMultipleInput,
    PartGroupInput,
    MmmmApplCatInput,
    SectionInput,
    MmmmReasonInput,
    MmmmReasonDtlInput,
    ManufacturerInput,
    PtCdInput,
    MoldNoInput,
    PartCatInput,
    MmmmLotInput,
    MmmmInspIndexInput,
    MmmmRequiredDocInput,
    UserInput,
    PartCodeInput,
    SupplierPartInput,
    PartSupplierInput,
    OqcResultDialog,
    MmmmRequestRouteForm,
    DelConfirmDialog,
    UserConfirmDialog,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    canEditEpson: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      headersSecondPart: [
        { text: '', value: 'action', align: 'center', divider: true, sortable: false, width: '50' },
        {
          text: this.$t('ELE0059'),
          value: 'partCd',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0057'), value: 'manufacturerCd', divider: true, sortable: false, cellClass: 'py-1' },
        { text: this.$t('ELE0054'), value: 'remarks', sortable: false, cellClass: 'py-1' },
      ],
      headersDoc: [
        { text: '', value: 'action', align: 'center', divider: true, sortable: false, width: '50' },
        {
          text: this.$t('ELE0241'),
          value: 'docCatName',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0035'), value: 'filePath', sortable: false, cellClass: 'py-1' },
      ],
      delConfirmDialog: false,
      userConfirmDialog: false,
      mode: '',
      settings: [
        {
          mode: SaveMode.TEMP_SAVE,
          url: '/mmmm/supplier/tempSave',
          isValidateChecker: false,
          canUploadFile: true,
        },
        {
          mode: SaveMode.REQUEST,
          url: '/mmmm/supplier/request',
          isValidateChecker: true,
          canUploadFile: true,
        },
        {
          mode: SaveMode.APPROVE,
          url: '/mmmm/supplier/approve',
          isValidateChecker: true,
          canUploadFile: true,
        },
        {
          mode: SaveMode.DENY,
          url: '/mmmm/supplier/deny',
          isValidateChecker: true,
          canUploadFile: true,
        },
        {
          mode: SaveMode.RETURN,
          url: '/mmmm/supplier/return',
          isValidateChecker: false,
          canUploadFile: false,
        },
      ],
      oqcResultDialog: false,
      inspResult: null,
    }
  },

  computed: {
    ...mapGetters(['isEpson']),
    enumCompanyCatCd: () => CompanyCatCd,
    enumOqcIqcCd: () => OqcIqcCd,
    enumRole: () => Role,
    enumInspStatusCd: () => InspStatusCd,

    loginUserCd: {
      get() {
        return this.$store.getters.userCd
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    canEditCompany: {
      get() {
        let result = false
        if (
          this.form.requestCompanyCatCd == CompanyCatCd.SUPPLIER &&
          this.form.requestCompanyCd == this.$store.getters.companyCd
        ) {
          result = true
        } else if (this.form.requestCompanyCatCd == CompanyCatCd.EPSON && this.isEpson) {
          result = true
        }
        return result
      },
    },
    canEditContents: {
      get() {
        let result = false

        if (!this.canEditCompany) {
          result = false
        } else if (!this.form.statusCd || this.form.statusCd == MmmmApprovalStatusCd.TEMP_SAVE) {
          result = true
        }

        return result
      },
    },
    canTempSave: {
      get() {
        return this.canEditContents
      },
    },
    canRequest: {
      get() {
        return this.canEditContents
      },
    },
    canDelete: {
      get() {
        return this.canTempSave && this.form.statusCd != ''
      },
    },
    canEditRoute: {
      get() {
        return this.canRequest || this.canApprove || this.canReturnToRequest
      },
    },
    canApprove: {
      get() {
        let result = false

        if (this.canEditCompany && this.form.statusCd == MmmmApprovalStatusCd.SUPPLIER_APPLYING) {
          const currentCheckedCat = this.form.currentCheckedCat
          const hasCheck = this.$store.getters.hasRole(Role.MMMM_CHECK)
          const hasApprove = this.$store.getters.hasRole(Role.MMMM_APPROVE)

          if (currentCheckedCat == MmmmWfCheckedCat.CHECK && hasCheck) {
            result = true
          } else if (currentCheckedCat == MmmmWfCheckedCat.APPROVE && hasApprove) {
            result = true
          } else {
            result = false
          }
        }
        return result
      },
    },
    canDeny: {
      get() {
        return this.canApprove
      },
    },
    canReturnToRequest: {
      get() {
        const hasCheck = this.$store.getters.hasRole(Role.MMMM_CHECK)
        const hasApprove = this.$store.getters.hasRole(Role.MMMM_APPROVE)
        return (
          this.form.statusCd == MmmmApprovalStatusCd.SUPPLIER_APPLYING &&
          this.canEditCompany &&
          (hasCheck || hasApprove)
        )
      },
    },
    canEditRemarks: {
      get() {
        return this.canEditContents || this.canReturnToRequest || this.canEditEpson
      },
    },
    hasGeneratedMmmmNo: {
      get() {
        let result = false
        if (this.form.mmmmNo) {
          result = true
        }
        return result
      },
    },
    secondProcess: {
      get() {
        return this.form.secondProcess
      },
    },
    requiredDocs: {
      get() {
        return this.form.requiredDocs
      },
    },
    companyCatCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(CompanyCatCd).map((item) => {
          return { name: item + ' : ' + this.$t(`company.companyCatcd.${item}`), value: item }
        })
      },
    },
    productTakeOutItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmProductTakeOut).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmProductTakeOut.${item}`), value: item }
        })
      },
    },
    injectionMCTypeItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmInjectionMCType).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmInjectionMCType.${item}`), value: item }
        })
      },
    },
    secondaryCategoryItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmSecondaryCategory).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmSecondaryCategory.${item}`), value: item }
        })
      },
    },
    paintingProcessItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmPaintingProcess).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmPaintingProcess.${item}`), value: item }
        })
      },
    },
    inkProcessItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmInkProcess).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmInkProcess.${item}`), value: item }
        })
      },
    },
  },

  methods: {
    onChangePartGroup() {
      this.form.ptCd = ''
      this.onChangePtCd(this.form.ptCd)
    },
    onChangeApplCat(model) {
      this.form.sampleAttachedQty = null
      if (model) {
        this.form.reqSampleAttached = model.reqSampleAttached
      } else {
        this.form.reqSampleAttached = false
      }
      this.form.reasonCd = ''
    },
    onChangeReasonDtlCd(value) {
      if (value) {
        this.getSeverity(this.form.applCatCd, value)
        this.getRequiredDocs(this.form.applCatCd, this.form.partCatCd, value)
      } else {
        this.form.severityCd = ''
        this.form.severityName = ''
      }
    },
    onChangePtCd(value) {
      if (value) {
        this.form.partCd = value
      } else {
        this.form.partCd = ''
      }
      this.form.moldNo = ''
      this.onChangePartCd(this.form.partCd)
    },
    onChangeMoldNo(model) {
      if (model) {
        this.form.partCd = model.partCd
      } else {
        this.form.partCd = ''
      }
      this.onChangePartCd(this.form.partCd)
    },
    onChangePartCd(value) {
      if (value) {
        this.form.partCd = value
      }
      this.form.lotNo = ''
      this.getPartInfo(this.form.partCd)
      this.onChangeLotNo()
    },
    onChangeLotNo() {
      this.form.oqcInspIndexNo = ''
    },
    async getPartInfo(partCd) {
      try {
        this.form.model = ''
        this.form.partCatCd = ''
        this.form.partName = ''

        if (partCd) {
          const params = {
            isLatest: true,
          }
          const element = await API.get(`/master/parts/${partCd}`, params)
          this.form.model = element.model
          this.form.partCatCd = element.partCatCd
          this.form.partName = element.partName
        }
      } catch (error) {
        // エラーメッセージは表示しない
      }

      this.getRequiredDocs(this.form.applCatCd, this.form.partCatCd, this.form.reasonDtlCd)
    },
    async getSeverity(applCatCd, reasonDtlCd) {
      try {
        this.form.severityCd = ''
        this.form.severityName = ''

        let params = {
          applCatCd: applCatCd,
          reasonDtlCd: reasonDtlCd,
        }
        const severity = await API.get('/master/mmmm/severity', params)
        if (severity) {
          this.form.severityCd = severity.severityCd
          this.form.severityName = severity.severityName
        }
      } catch (error) {
        // applCatCd = IPAの場合、severityはマスタに存在しないがエラーとしない
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0443') })))
        }
      }
    },
    async getRequiredDocs(applCatCd, partCatCd, reasonDtlCd) {
      try {
        this.requiredDocs.splice(0, this.requiredDocs.length)

        if (partCatCd && reasonDtlCd) {
          let params = {
            applCatCd: applCatCd,
            partCatCd: partCatCd,
            reasonDtlCd: reasonDtlCd,
            validOnly: true,
          }

          const elements = await API.get('/master/mmmm/requiredDocs', params)
          elements.forEach((element) => {
            const item = {
              docCatName: element['docCatName'],
              docCatCd: element['docCatCd'],
              filePath: '',
              file: null,
            }
            this.requiredDocs.push(item)
          })
        }
      } catch (error) {
        this.requiredDocs.splice(0, this.requiredDocs.length)
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0240') })))
      }
    },
    onAddSecondPart() {
      const item = {
        partCd: '',
        manufacturerCd: '',
        remarks: '',
      }
      this.secondProcess.parts.push(item)
    },
    onDeleteSecondPart(index) {
      this.secondProcess.parts.splice(index, 1)
    },
    onAddDoc() {
      const item = {
        docCatName: '',
        docCatCd: '',
        filePath: '',
        file: null,
      }
      this.requiredDocs.push(item)
    },
    onDeleteDoc(index) {
      this.requiredDocs.splice(index, 1)
    },
    async onClickTempSave() {
      // 一時保存
      this.mode = SaveMode.TEMP_SAVE
      this.form.requesterCd = this.loginUserCd
      await this._save()
    },
    async onClickRequest() {
      // 申請
      this.mode = SaveMode.REQUEST
      this.form.requesterCd = this.loginUserCd
      await this._save()
    },
    async onClickApprove() {
      // 承認
      this.mode = SaveMode.APPROVE
      await this._save()
    },
    async onClickDeny() {
      // 否認
      this.mode = SaveMode.DENY
      await this._save()
    },
    async onClickReturnToRequest() {
      // 申請者へ戻す
      this.mode = SaveMode.RETURN
      await this._save()
    },
    onClickDelete() {
      // 削除
      this.delConfirmDialog = true
    },
    async onProceedDelete() {
      // 削除
      try {
        this.delConfirmDialog = false

        this.startLoading()

        const body = {
          mmmmNo: this.form.mmmmNo,
          wfNo: this.form.wfNo,
          wfVersionNo: this.form.wfVersionNo,
        }

        await API.post('/mmmm/supplier/delete', body)
        this.setSuccessMessage(this.$t('MSG0014'))
        this.resetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0184') })))
      } finally {
        this.stopLoading()
      }
    },
    async onProceedSave() {
      // 保存を進める
      this.userConfirmDialog = false
      let checker = this.getCurrentChecker()
      checker.checkerCd = this.loginUserCd
      await this._save()
    },
    async _save() {
      // 保存
      try {
        this.startLoading()

        if (!(await this.validate())) {
          return
        }

        if (!this.hasGeneratedMmmmNo) {
          // S3のファイルパスに4M Noを付けたいので事前に採番
          const body = {
            applCatCd: this.form.applCatCd,
            sectionCd: this.form.sectionCd,
          }
          this.form.mmmmNo = await API.post('/mmmm/generate/mmmmNo', body)
        }

        if (this.setting.canUploadFile) {
          await this.uploadRequiredDocs()
        }

        const body = {
          mmmmNo: this.form.mmmmNo,
          wfNo: this.form.wfNo,
          wfVersionNo: this.form.wfVersionNo,
          currentRouteNo: this.form.currentRouteNo,
          currentRouteDtlNo: this.form.currentRouteDtlNo,
          requestCompanyCd: this.form.requestCompanyCd,
          requesterCd: this.form.requesterCd,
          accepterCd: this.form.accepterCd,
          routeNo: this.form.supplierRoute.routeNo,
          checkers: this.form.supplierRoute.checkers,
          applCatCd: this.form.applCatCd,
          applRequestByCd: this.form.applRequestByCd,
          sectionCd: this.form.sectionCd,
          reasonCd: this.form.reasonCd,
          reasonDtlCd: this.form.reasonDtlCd,
          remarks: this.form.remarks,
          severityCd: this.form.severityCd,
          manufacturerCd: this.form.manufacturerCd,
          issueDate: this.form.issueDate,
          targetUsageDate: this.form.targetUsageDate,
          tenNo: this.form.tenNo,
          partCd: this.form.partCd,
          lotNo: this.form.lotNo,
          drawingNo: this.form.drawingNo,
          sampleAttachedQty: this.form.sampleAttachedQty,
          oqcInspIndexNo: this.form.oqcInspIndexNo,
          plPr: this.form.plPr,
          paint: this.form.paint,
          secondProcess: this.secondProcess,
          print: this.form.print,
          requiredDocs: this.form.requiredDocs,
          requestComment: this.getCurrentChecker().remarks,
        }

        await API.post(this.setting.url, body)
        this.setSuccessMessage(this.$t('MSG0011', { p0: this.form.mmmmNo }))
        this.resetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0491') })))
      } finally {
        this.stopLoading()
      }
    },

    async uploadRequiredDocs() {
      // ドキュメントファイルをS3へアップロード
      for (let idx = 0; idx < this.requiredDocs.length; idx++) {
        if (this.requiredDocs[idx].file && this.requiredDocs[idx].filePath == '') {
          this.requiredDocs[idx].filePath = await Storage.uploadFile(
            this.requiredDocs[idx].file,
            'mmmm/' + this.form.mmmmNo + '/' + S3Root.MMMM_REQUIRE_DOC,
          )
          this.requiredDocs[idx].file = null
        }
      }
    },

    async validate() {
      // 入力チェック
      if (this.setting.isValidateChecker) {
        if (this.existsUserInRoute()) {
          this.setErrorMessage(this.$t('MSG0094'))
          return false
        }
        const checker = this.getCurrentChecker()
        if (checker && checker.checkerCd != this.loginUserCd) {
          this.userConfirmDialog = true
          return false
        }
      }

      return true
    },
    getCurrentRoute() {
      // 現在の処理ルート取得
      return this.form.supplierRoute
    },
    existsUserInRoute() {
      // 現在ルート詳細No以外で、ログインユーザーが指定されているかチェック
      return this.getCurrentRoute().checkers.some(
        (el) => el.routeDtlNo != this.form.currentRouteDtlNo && el.checkerCd == this.loginUserCd,
      )
    },
    getCurrentChecker() {
      // WFで指定されている現在の処理ユーザー取得
      return this.getCurrentRoute().checkers.find((el) => el.routeDtlNo == this.form.currentRouteDtlNo)
    },
    resetForm() {
      if (this.mode == SaveMode.TEMP_SAVE) {
        this.$emit('research', this.form.mmmmNo)
      } else {
        this.$emit('reset')
      }
    },
    async showInspResult() {
      try {
        this.inspResult = null

        this.startLoading()
        const element = await API.get(`/inspIndex/inspIndexes/${this.form.oqcInspIndexNo}`)
        this.inspResult = {
          inspIndexNo: element.inspIndexNo,
          inspCompanyCd: element.inspCompanyCd,
          lotNo: element.lotNo,
          inspCat1: element.inspCat1,
          inspCat2: element.inspCat2,
          lotDate: this.toLocalDate(element.inspCreateDatetime),
        }
        this.oqcResultDialog = true
      } catch (error) {
        this.oqcResultDialog = false
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
  },
}
</script>

<style scoped>
.form-mmmm-supplier .row > .col {
  padding: 8px;
}
</style>
